import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import resolveStringValue from '../../../../utils/components/_resolveStringValue';
import { DropListComposedProps, DropListOption } from '../DropList';

interface UseDropListValue<Value = string, OptionValue = Value> {
  options: DropListComposedProps<Value, OptionValue>['options'];
  value: DropListComposedProps<Value, OptionValue>['value'];
  emptyValue: DropListComposedProps<Value, OptionValue>['emptyValue'];
  isOpened: boolean;
  selectFirstOption: boolean;
  onValueChange: (value: OptionValue | Value, options: { selectedOption?: DropListOption<OptionValue>}) => void;
}

const useDropListValue = <Value, OptionValue>({ options, value, emptyValue, isOpened, selectFirstOption, onValueChange }: UseDropListValue<Value, OptionValue>) => {
  const getDefaultedValue = useCallback((newValue: Value) => {
    if (typeof newValue !== 'undefined' && value !== emptyValue) return newValue;
    if (selectFirstOption && options.length) return options[0].value;
    return emptyValue;
  }, [emptyValue, options, selectFirstOption, value]);

  const [internalValue, setInternalValue] = useState<Value>(() => getDefaultedValue(value) as Value);

  const selectedOptionIndexes = useMemo(() => (
    options.reduce((result, { value: optionValue = emptyValue, key }, index) => {
      if (Array.isArray(internalValue)) {
        const stringOptionValue = resolveStringValue(optionValue, key);
        if (internalValue.includes(optionValue) || (typeof optionValue === 'object' && internalValue.some((val) => resolveStringValue(val, key) === stringOptionValue))) {
          result.push(index);
        }
      } else if (
        optionValue === internalValue ||
        (typeof internalValue === 'object' && resolveStringValue(internalValue, key) === resolveStringValue(optionValue, key))
      ) {
        result.push(index);
      }
      return result;
    }, [])
  ), [internalValue, options, emptyValue]);

  const selectedOptions = useMemo(() => (
    selectedOptionIndexes.map((selectedOptionIndex) => options[selectedOptionIndex])
  ), [options, selectedOptionIndexes]);

  const [lastClosedValue, setLastClosedValue] = useState(internalValue);

  useEffect(() => {
    if (isOpened === false && lastClosedValue !== internalValue) {
      setLastClosedValue(internalValue);
    }
  }, [internalValue, isOpened, lastClosedValue]);

  useEffect(() => {
    if (value !== internalValue || (!selectedOptions.length && value === emptyValue)) {
      const newValue = getDefaultedValue(value);
      setInternalValue(newValue as Value);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getDefaultedValue, value]);

  const handleValueChange = useCallback((newValue: OptionValue | Value = emptyValue as Value, changeOptions: ChangeEvent | { type?: 'click' | 'change' | 'keydown'; selectedOption?: DropListOption<OptionValue> } = {}) => {
    setInternalValue(newValue as Value);

    if (onValueChange) {
      const newSelectedIndex = options.findIndex(({ value: optionValue = emptyValue }) => optionValue === newValue);
      const newSelectedOption = options[newSelectedIndex];
      onValueChange(newValue, { ...changeOptions, selectedOption: newSelectedOption });
    }
  }, [emptyValue, onValueChange, options]);

  return {
    internalValue,
    handleValueChange,
    selectedOptions,
    selectedOptionIndexes,
    lastClosedValue,
  };
};

export default useDropListValue;
